"use client";
import { UserAvatar } from "@cmp-src/data-display/UserAvatar/UserAvatar";
import type {
  DivisionsTree,
  GlobalSearchUserResult,
  IDedicatedResourceRef,
  ISchedule,
  OfficeWithFloors
} from "@gettactic/api";
import { cn } from "@gettactic/helpers";
import { useAuthenticated } from "@gettactic/hooks";
import { useCallback } from "react";
import { GlobalSearchListItemLayout } from "../GlobalSearchListItemLayout";
import { getOfficeAndFloor } from "../globalSearchOfficeUtils";
import { usePersonScheduleInfo } from "../hooks/useScheduleInfo";
import { OfficeFloorInfo } from "../OfficeFloorInfo";

interface PersonItemProps {
  person: GlobalSearchUserResult;
  schedules?: ISchedule[];
  isActive: boolean;
  onSelect: () => void;
  /** Clicking the dedicated-desk chip jumps to that desk on the map. */
  onDedicatedResourceClick?: (resource: IDedicatedResourceRef) => void;
  divisionCategories?: {
    offices: Record<string, OfficeWithFloors>;
    tree: DivisionsTree | null;
  };
}

export function PersonItem({
  person,
  schedules = [],
  isActive,
  onSelect,
  onDedicatedResourceClick,
  divisionCategories
}: PersonItemProps) {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();

  const getOfficeFloorInfo = useCallback(
    (officeId?: string | null, parentId?: string | null) => {
      const { officeName, floorName } = getOfficeAndFloor({
        officeId,
        parentId,
        divisionCategories,
        userOfficesById: authenticatedUser?.offices?.byId
      });
      return { officeName, floorName };
    },
    [divisionCategories, authenticatedUser]
  );

  const scheduleInfo = usePersonScheduleInfo(schedules, getOfficeFloorInfo);

  // Owner of a dedicated desk — with no schedule in the searched window,
  // their desk is still their place in the office.
  const dedicatedResource = person.dedicated_resources?.[0];
  const dedicatedOfficeFloor = dedicatedResource
    ? getOfficeFloorInfo(
        dedicatedResource.office_id,
        dedicatedResource.parent_id
      )
    : null;

  return (
    <GlobalSearchListItemLayout
      isActive={isActive}
      className={cn("gap-4 p-2")}
      onClick={onSelect}
    >
      <UserAvatar
        user={{
          name: person.name,
          profile_photo_url: person.profile_photo_url
        }}
        size="md"
        className="text-xs"
      />

      <div className="flex min-w-0 flex-1 flex-col gap-1.8 overflow-hidden">
        <div className="flex items-center justify-between gap-2">
          <h4 className="flex min-w-0 items-center gap-1 truncate font-inter font-semibold text-[14px] text-neutral-1000">
            {person.name}
          </h4>
          {scheduleInfo?.resourceName ? (
            <span className="flex-shrink-0 rounded-[5px] border border-neutral-100 px-1.5 py-1 font-inter font-medium text-[12px] text-neutral-1000">
              {scheduleInfo.resourceName}
            </span>
          ) : scheduleInfo?.type === "current" ? (
            // Active schedule whose desk couldn't be resolved — the person
            // IS in the office, we just can't name the spot.
            <span className="flex-shrink-0 rounded-[5px] border border-neutral-100 px-1.5 py-1 font-inter font-medium text-[12px] text-neutral-1000">
              In office
            </span>
          ) : dedicatedResource ? (
            <button
              type="button"
              className="flex-shrink-0 rounded-[5px] border border-indigo-100 bg-indigo-50 px-1.5 py-1 font-inter font-medium text-[12px] text-primary hover:bg-indigo-100"
              onClick={(event) => {
                // The whole row navigates to the person — the chip
                // navigates to their desk instead.
                event.stopPropagation();
                onDedicatedResourceClick?.(dedicatedResource);
              }}
            >
              {dedicatedResource.name}
            </button>
          ) : (
            <span className="flex-shrink-0 rounded-[5px] border border-neutral-100 px-1.5 py-1 font-inter font-medium text-[12px] text-neutral-400">
              Not in office
            </span>
          )}
        </div>
        {person.title && (
          <p className="truncate font-inter font-medium text-[12px] text-neutral-700">
            {person.title}
          </p>
        )}
        {scheduleInfo ? (
          <OfficeFloorInfo
            officeName={scheduleInfo.officeName}
            floorName={scheduleInfo.floorName}
          />
        ) : dedicatedOfficeFloor ? (
          <OfficeFloorInfo
            officeName={dedicatedOfficeFloor.officeName}
            floorName={dedicatedOfficeFloor.floorName}
          />
        ) : null}
      </div>
    </GlobalSearchListItemLayout>
  );
}
